<script setup lang="ts">
import { computed } from "vue";

interface Props {
    gauge?: number;
    value: number;
    min?: number;
    max?: number;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    ariaErrorMessage?: string;
}

const {
    gauge = 5,
    value,
    min = 0,
    max = 100,
    ariaLabel = "",
    ariaLabelledBy = "",
    ariaDescribedBy = "",
    ariaErrorMessage = "",
} = defineProps<Props>();

const size = 100;

const valuePercent = computed(() => (100 * (value - min)) / (max - min));
const normalizedValuePercent = computed(() => {
    if (valuePercent.value > 100) return 100;
    if (valuePercent.value < 0) return 0;
    return valuePercent.value || 0; // valuePercent could be NaN
});
const circumference = computed(() => Math.PI * radius.value * 2);
const radius = computed(() => size / 2 - gauge);
const arcLength = computed(
    () => ((100 - normalizedValuePercent.value) * circumference.value) / 100
);
</script>

<template>
    <div
        class="circle-progress"
        role="progressbar"
        :aria-valuemax="max"
        :aria-valuemin="min"
        :aria-valuenow="value ?? normalizedValuePercent"
        :aria-label="ariaLabel"
        :aria-labelledby="ariaLabelledBy"
        :aria-describedby="ariaDescribedBy"
        :aria-errormessage="ariaErrorMessage"
    >
        <div v-if="$slots.default" class="content">
            <slot :percent="normalizedValuePercent" :value="value" />
        </div>
        <svg
            :viewBox="`0 0 ${size} ${size}`"
            class="chart"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                :cx="size / 2"
                :cy="size / 2"
                :r="radius"
                :stroke-dasharray="circumference"
                :stroke-width="gauge"
                class="progress-background"
                stroke-dashoffset="0"
            />
            <circle
                :cx="size / 2"
                :cy="size / 2"
                :r="radius"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="arcLength"
                :stroke-width="gauge"
                class="progress-track"
            />
        </svg>
    </div>
</template>

<style scoped>
.circle-progress {
    display: grid;
    grid: 100% / 100%;
    border-radius: 50%;

    .content {
        display: grid;
        place-items: center;
        grid-column: 1/2;
        grid-row: 1/2;
        z-index: 1;
    }

    .chart {
        transform: rotate(-90deg);
        grid-column: 1/2;
        grid-row: 1/2;
        z-index: 0;
    }

    circle {
        stroke-linecap: square;
    }

    .progress-background {
        fill: transparent;
        stroke: var(--color-progress-background);
    }

    .progress-track {
        fill: transparent;
        stroke: var(--color-progress-track);
    }
}
</style>
